<template>
  <div class="report-page">
    <div class="filter-card">
      <div class="fc-row fc-row-primary">
        <div class="field">
          <label class="field-lbl">ช่วงวันที่</label>
          <date-picker
            v-model="dateRange"
            type="date"
            range
            format="DD MMM YY"
            :shortcuts="dateRangeShortcuts"
            :disabled-date="notAfterToday"
            placeholder="เลือกวันที่ต้องการค้นหา"
            input-class="form-control form-control-sm field-input-native"
          ></date-picker>
        </div>

        <div class="field">
          <label class="field-lbl">แพทย์</label>
          <b-form-select
            v-model="selectedDoctor"
            :options="doctorsList"
            text-field="fullName"
            value-field="fullName"
            size="sm"
            class="field-input-native"
          >
            <template #first>
              <b-form-select-option :value="null">
                แพทย์ทั้งหมด
              </b-form-select-option>
            </template>
          </b-form-select>
        </div>

        <b-button
          variant="primary"
          class="btn-primary-lg"
          @click="fetchReport"
        >
          แสดงข้อมูล
        </b-button>
      </div>

      <div class="fc-row fc-row-secondary">
        <div class="field">
          <label class="field-lbl">รายการรักษา</label>
          <b-input-group>
            <multiselect
              class="col p-0 multiselect-h-36px"
              :style="{ minHeight: '36px' }"
              v-model="selectedTx"
              :options="getGroupedTreatListNhso"
              :limit="5"
              :multiple="true"
              :close-on-select="false"
              group-values="libs"
              group-label="category"
              :group-select="true"
              placeholder="ค้นหาการรักษาที่ต้องการกรอง"
              :custom-label="customTxDrugsLabel"
              track-by="nameTh"
              selectLabel
              selectedLabel
              deselectLabel
            >
              <template #option="props">
                <span v-if="props.option.$isLabel">
                  {{ props.option.$groupLabel }}
                </span>
              </template>
              <template #clear="props">
                <div
                  v-if="selectedTx.length"
                  class="multiselect__clear"
                  @mousedown.prevent.stop="clearTxFilter(props)"
                ></div>
              </template>
              <span slot="noResult">ไม่พบรายการ กรุณาเปลี่ยนคำค้นหา</span>
            </multiselect>
            <b-input-group-append>
              <b-button
                size="sm"
                class="fi-fav"
                @click="$refs.nameInputModal.openModal()"
              >
                <i class="far fa-star"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>

        <div class="field">
          <label class="field-lbl">ตัวกรองที่บันทึก</label>
          <FavFilter
            ref="favFilter"
            storageName="favouriteFilter"
            :height="36"
            @listClicked="applySelectedTx"
          />
        </div>
      </div>
    </div>

    <div
      id="report-elements"
      ref="fullscreenRoot"
      :class="{ 'my-fullscreen': isFullscreen }"
    >
      <div class="my-fullscreen-bar">
        <span class="title">{{ reportTitle }}</span>
        <div class="meta">
          <span v-if="dateRange[0] && dateRange[1]">
            วันที่: {{ formatDate(dateRange[0]) }} – {{ formatDate(dateRange[1]) }}
          </span>
          <span v-if="branchInfo && branchInfo.name">
            สาขา: {{ branchInfo.name }}
          </span>
        </div>
        <span class="spacer"></span>
        <b-button
          size="sm"
          variant="outline-secondary"
          :title="fullscreenLabel"
          :aria-label="fullscreenLabel"
          @click="toggleFullscreen"
        >
          <i class="fas fa-times"></i>
        </b-button>
      </div>
      <div class="toolbar">
        <b-pagination
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          size="sm"
          first-number
          last-number
          pills
          limit="10"
          class="mb-0"
        ></b-pagination>

        <div class="toolbar-right">
          <b-dropdown size="sm" variant="outline-success" right>
            <template #button-content>
              <i class="fas fa-download"></i>
            </template>
            <b-dropdown-item @click="excelExport('branchItemsIncomeTable')">
              <i class="fas fa-file-excel mr-2"></i>Excel
            </b-dropdown-item>
            <b-dropdown-item @click="pdfExport('branchItemsIncomeTable')">
              <i class="fas fa-file-pdf mr-2"></i>PDF
            </b-dropdown-item>
          </b-dropdown>
          <b-button
            size="sm"
            :variant="fullscreenButtonVariant"
            :title="fullscreenLabel"
            :aria-label="fullscreenLabel"
            @click="toggleFullscreen"
          >
            <i class="fas" :class="fullscreenIcon"></i>
          </b-button>
        </div>
      </div>

      <b-table
        id="branchItemsIncomeTable"
        ref="branchItemsIncomeTable"
        class="my-0 sticky-footer report-table"
        small
        sort-icon-left
        hover
        head-variant="light"
        :busy="tableBusy"
        :items="filteredList"
        :fields="fields"
        sort-by="creationDt"
        :sort-desc="true"
        v-model="visibleRows"
        :responsive="true"
        caption="Double click เพื่อดูใบเสร็จ/ DF และ CF คำนวนจากการรับชำระของคนไข้ในแต่ละครั้ง"
        :sticky-header="tableHeight"
        :per-page="perPage"
        :current-page="currentPage"
        show-empty
        @row-dblclicked="openReceipt"
      >
        <template #cell(index)="row">{{ row.item.row }}.</template>
        <template #cell(creationDt)="row">{{ formatDate(row.value) }}</template>
        <template #cell(time)="row">{{ formatTime(row.item.creationDt) }}</template>
        <template #cell(treatType)="row">{{ formatType(row.value) }}</template>

        <template #custom-foot>
          <b-tr variant="secondary" class="text-bold text-right">
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td class="text-left">รวม</td>
            <td>{{ formatNumber(sum("price")) }}</td>
          </b-tr>
        </template>
        <template #empty>
          <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
        </template>
      </b-table>
    </div>
    <NameInput ref="nameInputModal" @submitName="addFav" />
    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { eventBus } from "@/main";

import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import moment from "moment";

import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";

import FavFilter from "@/components/FavFilter";
import NameInput from "@/components/modal/NameInput";
import reportFullscreenMixin from "@/mixins/reportFullscreenMixin";

export default {
  name: "BranchItemsIncomeReport",
  mixins: [reportFullscreenMixin],
  components: {
    Dialog,
    Loading,
    FavFilter,
    NameInput,
  },
  data() {
    return {
      isLoading: false,
      reportTitle: "รายงาน สปสช. (NHSO)",
      reportNameEn: "NHSO",

      dateRange: [],
      selectedDoctor: null,

      //table
      tableBusy: false,
      itemsList: [],
      fields: [
        {
          key: "index",
          label: "",
        },
        {
          key: "creationDt",
          label: "วันที่ออกใบเสร็จ",
          sortable: true,
          class: "narrow-spacing",
        },
        {
          key: "time",
          label: "เวลา",
          class: "narrow-spacing",
        },
        {
          key: "doctorFullName",
          label: "แพทย์",
          class: "narrow-spacing",
        },
        {
          key: "dn",
          label: "DN.",
          class: "narrow-spacing",
        },
        {
          key: "patientFullName",
          label: "ชื่อคนไข้",
          class: "narrow-spacing",
        },
        {
          key: "itemName",
          label: "รายการรักษา",
          class: "narrow-spacing",
        },

        {
          key: "price",
          label: "ค่ารักษา",
          class: "text-right",
          formatter: "formatNumber",
        },
      ],
      visibleRows: [],
      itemsType: [],
      selectedTx: [],
      selectedDrug: [],
      favTxValue: [],

      // pagination
      currentPage: 1,
      perPage: 100,

      tableTopPos: null,
    };
  },
  created() {
    this.init();
  },
  computed: {
    ...mapGetters({
      userInfo: "moduleUser/getUserInfo",
      doctorsList: "moduleUser/getDoctorList",
      getTreatBy: "moduleAppointment/getTreatBy",
      getReportDateRange: "moduleReport/getReportDateRange",
      getGroupedTreatListNhso: "moduleAppointment/getGroupedTreatListNhso", //ย้าย grouping เข้าไปใน appointment module
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      getNhsoMtreatIds: "moduleAppointment/getNhsoMtreatIds",
      branchInfo: "moduleBranch/getBranchInfo",
      clinicInfo: "moduleBranch/getClinicInfo",
    }),
    filterItemId() {
      return this.selectedTx.map(i => i.id);
    },
    filterDrugId() {
      return this.selectedDrug.map(i => i.id);
    },
    filteredList() {
      return this.filteredAndGroupedList
        .filter(
          item =>
            (this.filterItemId.length === 0 ||
              this.filterItemId.includes(parseInt(item.txItemId))) &&
            (this.filterDrugId.length === 0 ||
              this.filterDrugId.includes(parseInt(item.drugItemId))) &&
            (!this.selectedDoctor ||
              item.doctorFullName === this.selectedDoctor)
        )
        .slice(0)
        .map((i, index) => ({ ...i, row: index + 1 }));
    },

    filteredAndGroupedList() {
      // Step 1: Filter the items (uncomment if you want to apply NHSO filtering)
      const filteredItems = this.itemsList.filter(
        item =>
          item.treatType === 1 &&
          this.getNhsoMtreatIds.includes(parseInt(item.txItemId))
      );
      // const filteredItems = this.itemsList;

      // Step 2: Group by receiptNo
      const groupedItems = filteredItems.reduce((acc, item) => {
        if (!acc[item.receiptNo]) {
          acc[item.receiptNo] = [];
        }
        acc[item.receiptNo].push(item);
        return acc;
      }, {});

      // Step 3: Combine grouped items and set price to 700
      return Object.values(groupedItems).map(group => {
        if (group.length === 1) {
          return { ...group[0], price: 700 };
        } else {
          const combinedItem = { ...group[0], price: 700 };
          for (let i = 1; i < group.length; i++) {
            combinedItem.itemName = `${combinedItem.itemName}, ${group[i].itemName}`;
          }
          return combinedItem;
        }
      });
    },
    startDt() {
      return this.dateRange[0]
        ? moment(this.dateRange[0]).startOf("day").format()
        : "";
    },
    endDt() {
      return this.dateRange[1]
        ? moment(this.dateRange[1]).endOf("day").format()
        : "";
    },
    today() {
      let today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    rows() {
      return this.filteredList.length;
    },
    tableHeight() {
      if (this.isFullscreen) return "100%";
      return `calc(100vh - ${this.tableTopPos + 40}px)`;
    },
  },
  watch: {},
  methods: {
    ...mapActions({
      fetchReportBranchReceiptItem: "moduleReport/fetchReportBranchReceiptItem",
      setStateReportDateRange: "moduleReport/setStateReportDateRange",
    }),
    init() {},
    moment() {
      return moment();
    },
    formatDate(date) {
      if (date) {
        return moment(date).locale("th").format("D MMM YY");
      } else {
        return "";
      }
    },
    formatTime(date) {
      return date ? moment(date).locale("th").format("HH:mm") : "";
    },
    formatNumber(value) {
      if (value === null || value === undefined || isNaN(value)) return "";
      return Number(value).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    resetDateRange() {
      this.dateRange = {
        startDate: null,
        endDate: null,
      };
    },

    showDialogToast(variant, textDetail) {
      this.$refs.Dialog.showToast(variant, textDetail);
    },
    sum(arg) {
      return this.filteredList.reduce((accum, item) => accum + item[arg], 0);
    },
    generatePDf() {
      eventBus.$emit(
        "generatePDF",
        this.$refs["branchItemsIncomeTable"].$el,
        this.$refs.header,
        this.$refs.footer
      );
    },
    sendEmail() {
      //รอ api
    },
    fetchReport() {
      if (this.startDt === "" || this.endDt === "") return;

      this.setStateReportDateRange(this.dateRange);
      this.isLoading = true;
      this.fetchReportBranchReceiptItem({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        startDt: this.startDt,
        endDt: this.endDt,
      })
        .then(res => {
          this.itemsList = res.data.report;
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async excelExport(id) {
      this.perPage = null;
      await this.$nextTick();

      exportTableToExcel({
        tableId: id,
        reportTitle: this.reportTitle,
        reportNameEn: this.reportNameEn,
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        doctorName: this.selectedDoctor || "",
        branchName: this.branchInfo && this.branchInfo.name,
        clinicName: this.clinicInfo && this.clinicInfo.name,
      });

      this.perPage = 100;
    },
    async pdfExport(id) {
      this.perPage = null;
      await this.$nextTick();

      exportReportToPdf({
        tableId: id,
        reportTitle: this.reportTitle,
        reportNameEn: this.reportNameEn,
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        doctorName: this.selectedDoctor || "",
        branchName: this.branchInfo && this.branchInfo.name,
        clinicName: this.clinicInfo && this.clinicInfo.name,
      });

      this.perPage = 100;
    },

    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
    openReceipt(data) {
      eventBus.$emit("openReceipt", data);
    },
    customTxDrugsLabel(option) {
      if (option.nameTh == option.nameEn) {
        return `${option.code ? "[" + option.code + "]-" : ""}${option.nameTh}`;
      } else {
        return `${option.code ? "[" + option.code + "]-" : ""}${option.nameTh}${
          option.nameEn ? " / " + option.nameEn : ""
        }`;
      }
    },
    clearTxFilter(prop) {
      this.selectedTx = [];
    },
    clearDrugFilter(prop) {
      this.selectedDrug = [];
    },
    applySelectedTx({ selectedTx, selectedDrug }) {
      this.selectedTx = selectedTx.flat();
      this.selectedDrug = selectedDrug.flat();
    },
    addFav(e) {
      let data = {
        text: e,
        selectedTx: this.selectedTx,
        selectedDrug: this.selectedDrug,
      };
      this.$refs.favFilter.addItem(data);
    },
  },
  async mounted() {
    if (this.getReportDateRange[0] && this.getReportDateRange[1]) {
      this.dateRange = this.getReportDateRange;
    } else {
      this.dateRange = [this.today, this.today];
    }
    await this.$nextTick();
    this.tableTopPos = document
      .querySelector("#branchItemsIncomeTable")
      ?.getBoundingClientRect()?.top;

    eventBus.$on("afterCancelReceipt", e => {
      this.fetchReport();
    });
  },
  updated() {},
};
</script>

<style >
</style>

<style >
.multiselect-h-38px .multiselect__tags {
  min-height: 38px;
}
.multiselect-h-31px .multiselect__tags {
  min-height: 31px;
}
</style>
